// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10
import React, { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { DatePickerField, Input, Select } from "../../../../../../_metronic/_partials/controls";
import { formatAddressInline, isAddressValid } from "../../../../../_utils/addressUtils";
import { formatDisplayNameIntl } from "../../../../../_utils/userUtils";
import { NumberInput } from "../../../../../_utils/formUtils";
import { FormattedMessage, useIntl } from "react-intl";
import { LEAD_STATUS } from "../../../../../_utils/listUtils";
import { AddressEditForm } from "../../../../../_components/AddressEditForm";
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks";
import { canReadAll } from "../../../../../_utils/authUtils";
import cn from "clsx";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../../../_metronic/_helpers";
import { DateUtils } from "../../../../../_utils/DateUtils";
import { Checkbox } from "../../../../../_components/Checkbox";
import { fetchSettings } from "../../../../Settings/_redux/settingsActions";
import logout from "../../../../Auth/pages/Logout";
import { SUFFIX_EURO_CURRENCY } from "../../../../../_utils/suffixUtils";
import { useLeadContext } from "./LeadContext";
import LeadPriceIndexSelector from "./LeadPriceIndexSelector";
import WarningModal from "../../../../../_components/WarningModal";

export function LeadEditForm({ lead, submitLead, saveLeadFields, disabled }) {
  const intl = useIntl();

  const [showModal, setShowModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const { groups, session, generalSettings } = useAppSelector((state) => ({
    groups: state.auth.groups,
    session: state.auth.session,
    generalSettings: state.settings.settingsForEdit.saved,
  }));

  const { initialCredit, remainingCredit } = useLeadContext();
  const dispatch = useAppDispatch();

  const settings = lead?.project?.projectOwner ?? generalSettings;

  useEffect(() => {
    if (!settings) {
      fetchSettings(dispatch).catch(() => {
        logout();
      });
    }
  }, [settings]);

  // Validation schema
  const LeadEditSchema = Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.LEAD.NAME.REQUIRED" }))
      .min(1, `${intl.formatMessage({ id: "AUTH.VALIDATION.MIN_CHARACTER" })} 1`)
      .max(250, `${intl.formatMessage({ id: "AUTH.VALIDATION.MAX_CHARACTER" })} 250`),
  });

  const hasInvoice = lead.financialDocuments?.some((file) => file.fileType === "INVOICE") ?? false;

  const customHandleChange = (e) => (handleChange) => {
    if (!!saveLeadFields) {
      if (
        e.target.name === "sellingPrice" &&
        lead.sellingPrice !== e.target.value &&
        lead.id &&
        hasInvoice
      ) {
        setShowWarningModal(!showWarningModal);
        return;
      }
      handleChange(e);
      saveLeadFields(e.target.name, e.target.value);
      if (e.target.name === "eligibleReducedVat" && e.target.value === false) {
        saveLeadFields("authorisationCode", "");
        saveLeadFields("authorisationDate", "");
      }
    }
  };

  const constructionPrice =
    lead.sellingPrice - (lead.shareOfLandSellingPrice + lead.architectEngineeringFees);
  const constructionPriceWithVat =
    (lead.eligibleReducedVat && lead.constructionPriceWithReducedVat
      ? lead.constructionPriceWithReducedVat * (1 + (settings?.reducedVat ?? 0))
      : 0) +
    (constructionPrice -
      (lead.eligibleReducedVat ? lead.constructionPriceWithReducedVat ?? 0 : 0)) *
      (1 + (settings?.defaultVat ?? 0));

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={lead}
        validationSchema={LeadEditSchema}
        onSubmit={() => {
          if (!disabled) {
            submitLead();
          }
        }}
      >
        {({ values, handleChange, setFieldValue }) => (
          <>
            <Form className="form form-label-right" data-cy="form-lead">
              <WarningModal
                displayModal={showWarningModal}
                modalTitle="LEAD.PRICE_CHANGE.WARNING_MODAL.TITLE"
                modalDescription="LEAD.PRICE_CHANGE.WARNING_MODAL.DESCRIPTION"
                setShowModal={setShowWarningModal}
              />
              <div className="form-group row">
                <div className={cn(canReadAll(groups, session, "USER") ? "col-lg-9" : "col-lg-12")}>
                  <Field
                    name="name"
                    data-cy="input-lead-name"
                    component={Input}
                    placeholder={intl.formatMessage({
                      id: "LEAD.TITLE.NAME",
                    })}
                    label={intl.formatMessage({
                      id: "LEAD.TITLE.NAME",
                    })}
                    onChange={(e) => customHandleChange(e)(handleChange)}
                    disabled={disabled}
                  />
                </div>
                {canReadAll(groups, session, "USER") && (
                  <div className="col-lg-3">
                    <Select
                      data-cy="select-status"
                      name="status"
                      label={intl.formatMessage({
                        id: "COMMON.STATUS",
                      })}
                      onChange={(e) => customHandleChange(e)(handleChange)}
                      disabled={disabled}
                      value={lead.status}
                    >
                      {Object.keys(LEAD_STATUS).map((value) => {
                        /* Hide "WON" option if already won once */
                        if (
                          value === "WON" &&
                          lead?.product?.leadStatus?.WON >= 1 &&
                          (!!lead?.id ? lead?.status !== "WON" : true)
                        )
                          return null;

                        return (
                          <option key={value} value={value} disabled={LEAD_STATUS[value].disabled}>
                            {LEAD_STATUS[value].name
                              ? intl.formatMessage({ id: LEAD_STATUS[value].name })
                              : LEAD_STATUS[value].name}
                          </option>
                        );
                      })}
                    </Select>
                  </div>
                )}
              </div>
              <div className="form-group row">
                <div className="col-sm-6 col-md-3">
                  <label>
                    <FormattedMessage id="INVOICE.PRICE.EXCL_TAX" />
                  </label>
                  <NumberInput
                    className="form-control text-right"
                    data-cy="input-lead-selling-price"
                    value={values.sellingPrice}
                    suffix={SUFFIX_EURO_CURRENCY}
                    decimalScale={2}
                    onValueChange={(e) =>
                      customHandleChange({
                        target: {
                          name: "sellingPrice",
                          value: e.floatValue || 0,
                        },
                      })(handleChange)
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="col-sm-6 col-md-3">
                  <label>
                    <FormattedMessage id="PRODUCT.INFO.SHARE_OF_LAND" />
                  </label>
                  <NumberInput
                    className="form-control text-right"
                    data-cy="input-lead-share-of-land-selling-price"
                    value={values.shareOfLandSellingPrice}
                    suffix={SUFFIX_EURO_CURRENCY}
                    decimalScale={2}
                    onValueChange={(e) =>
                      customHandleChange({
                        target: {
                          name: "shareOfLandSellingPrice",
                          value: e.floatValue || 0,
                        },
                      })(handleChange)
                    }
                    disabled={disabled}
                  />
                </div>
                <div className="col-sm-6 col-md-3">
                  <label>
                    <FormattedMessage id="PRODUCT.INFO.ARCHITECT_ENGINEERING_FEES" />
                  </label>
                  <NumberInput
                    className="form-control text-right"
                    data-cy="input-lead-architect-engineering-fees"
                    value={values.architectEngineeringFees}
                    suffix={SUFFIX_EURO_CURRENCY}
                    decimalScale={2}
                    onValueChange={(e) =>
                      customHandleChange({
                        target: {
                          name: "architectEngineeringFees",
                          value: e.floatValue || 0,
                        },
                      })(handleChange)
                    }
                    disabled={disabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-auto">
                  <label>
                    <FormattedMessage id="PRODUCT.INFO.CONSTRUCTION_PRICE" />
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="layout-tooltip" className="tooltip-auto-width">
                          <FormattedMessage id="INVOICE.PRICE.EXCL_TAX" /> - (
                          <FormattedMessage id="PRODUCT.INFO.SHARE_OF_LAND" /> +{" "}
                          <FormattedMessage id="PRODUCT.INFO.ARCHITECT_ENGINEERING_FEES" />)
                        </Tooltip>
                      }
                    >
                      <span className="svg-icon svg-icon-md svg-icon-info ml-2">
                        <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
                      </span>
                    </OverlayTrigger>
                  </label>
                  <div>
                    <NumberInput
                      data-cy="input-lead-construction-price"
                      displayType="text"
                      value={constructionPrice}
                      decimalScale={2}
                      suffix={SUFFIX_EURO_CURRENCY}
                      className="form-control font-weight-bold text-right border-0 px-0"
                    />
                  </div>
                </div>
                <div className="col-auto">
                  <label>
                    <FormattedMessage id="LEAD.CONSTRUCTION_PRICE_INCL_VAT" />
                  </label>
                  <div>
                    <NumberInput
                      data-cy="input-lead-construction-price-incl-vat"
                      displayType="text"
                      value={constructionPriceWithVat}
                      decimalScale={2}
                      suffix={SUFFIX_EURO_CURRENCY}
                      className="form-control font-weight-bold text-right border-0 px-0"
                    />
                  </div>
                </div>
              </div>
              {!disabled && (
                <Checkbox
                  customClasses="mb-4 mt-2"
                  name="eligibleReducedVat"
                  label="LEAD.ELIGIBLE_FOR_REDUCED_VAT"
                  onChange={(e) => {
                    customHandleChange({
                      target: { name: e.target.name, value: e.target.checked },
                    })(handleChange);
                  }}
                />
              )}
              {values.eligibleReducedVat && !disabled && (
                <>
                  <div className="form-group row">
                    <div className="col-sm-6 col-md-3">
                      <Field
                        name="authorisationCode"
                        data-cy="input-lead-authorisation-code"
                        component={Input}
                        label={intl.formatMessage({
                          id: "LEAD.AUTHORISATION_CODE",
                        })}
                        onChange={(e) => customHandleChange(e)(handleChange)}
                        disabled={disabled}
                      />
                    </div>
                    <div className="col-sm-6 col-md-3 d-flex flex-column">
                      <Field
                        className={
                          "form-control react-datepicker-ignore-onclickoutside no-validation"
                        }
                        name="authorisationDate"
                        showTimeSelect={false}
                        placeholderText={DateUtils.format(new Date(), intl, false)}
                        component={DatePickerField}
                        withFeedbackLabel={false}
                        dateFormat="dd/MM/yyyy"
                        onChange={(val) =>
                          customHandleChange({
                            target: {
                              name: "authorisationDate",
                              value: val?.toISOString() ?? null,
                            },
                          })(handleChange)
                        }
                        label={intl.formatMessage({
                          id: "LEAD.AUTHORISATION_DATE",
                        })}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-sm-6 col-md-4">
                      <label>
                        <FormattedMessage id="LEAD.CONSTRUCTION_PRICE_WITH_REDUCED_VAT" />
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="layout-tooltip" className="tooltip-auto-width">
                              <FormattedMessage id="COMMON.EXCL_VAT" />
                            </Tooltip>
                          }
                        >
                          <span className="svg-icon svg-icon-md svg-icon-info ml-2">
                            <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
                          </span>
                        </OverlayTrigger>
                      </label>
                      <NumberInput
                        className="form-control text-right"
                        data-cy="input-lead-construction-price-with-reduced-vat"
                        value={values.constructionPriceWithReducedVat}
                        max={constructionPrice}
                        suffix={SUFFIX_EURO_CURRENCY}
                        decimalScale={2}
                        onValueChange={(e) =>
                          customHandleChange({
                            target: {
                              name: "constructionPriceWithReducedVat",
                              value: e.floatValue || 0,
                            },
                          })(handleChange)
                        }
                        disabled={disabled}
                      />
                      {values.constructionPriceWithReducedVat > constructionPrice && (
                        <div className="text-danger invalid-feedback d-block">
                          <FormattedMessage id="LEAD.PRICE_HIGHER_THAN_CONSTRUCTION_PRICE" />
                        </div>
                      )}
                    </div>
                    <div className="col-auto">
                      <label>
                        <FormattedMessage id="LEAD.INITIAL_CREDIT" />
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="layout-tooltip" className="tooltip-auto-width">
                              <FormattedMessage id="LEAD.CONSTRUCTION_PRICE_WITH_REDUCED_VAT" /> * (
                              <FormattedMessage id="COMPANY.DEFAULT_VAT" /> -{" "}
                              <FormattedMessage id="COMPANY.REDUCED_VAT" />)
                            </Tooltip>
                          }
                        >
                          <span className="svg-icon svg-icon-md svg-icon-info ml-2">
                            <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
                          </span>
                        </OverlayTrigger>
                      </label>
                      <NumberInput
                        data-cy="input-lead-initial-credit"
                        displayType="text"
                        className="form-control border-0 font-weight-bold text-right px-0"
                        value={initialCredit}
                        suffix={SUFFIX_EURO_CURRENCY}
                        decimalScale={2}
                      />
                    </div>
                    <div className="col-auto">
                      <label>
                        <FormattedMessage id="LEAD.REMAINING_CREDIT" />
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="layout-tooltip" className="tooltip-auto-width">
                              <FormattedMessage id="LEAD.REMAINING_CREDIT_DESCRIPTION" />
                            </Tooltip>
                          }
                        >
                          <span className="svg-icon svg-icon-md svg-icon-info ml-2">
                            <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
                          </span>
                        </OverlayTrigger>
                      </label>
                      <NumberInput
                        data-cy="input-lead-remaining-credit"
                        displayType="text"
                        value={remainingCredit}
                        decimalScale={2}
                        suffix={SUFFIX_EURO_CURRENCY}
                        className="form-control font-weight-bold text-right border-0 px-0"
                      />
                    </div>
                  </div>
                </>
              )}
              {lead.project && (
                <LeadPriceIndexSelector
                  lead={lead}
                  settings={settings}
                  showModal={showModal}
                  setShowModal={setShowModal}
                  intl={intl}
                  values={values}
                  handleChange={handleChange}
                  customHandleChange={customHandleChange}
                />
              )}
              {lead.users && (
                <>
                  <h3 className="mt-14">
                    <FormattedMessage id="INVOICE.DETAILS" />
                  </h3>
                  <hr className="mt-0" />
                  <div className="form-group row">
                    <div className="col-lg-12">
                      <Field
                        name="subject"
                        data-cy="input-subject-name"
                        component={Input}
                        placeholder={`${lead?.product?.projectName || ""}${
                          lead?.product?.name ? `, ${lead?.product?.name}` : ""
                        }`}
                        label={intl.formatMessage({
                          id: "LEAD.TITLE.SUBJECT",
                        })}
                        onChange={(e) => customHandleChange(e)(handleChange)}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-lg-12">
                      <Select
                        name="invoiceAddressType"
                        label={intl.formatMessage({
                          id: "ADDRESS.TYPE",
                        })}
                        onChange={(e) => customHandleChange(e)(handleChange)}
                        disabled={disabled}
                        value={lead.invoiceAddressType}
                      >
                        {lead.users.map((value, index) => (
                          <option
                            key={index}
                            value={value.id}
                            disabled={!isAddressValid(value.address)}
                          >
                            {`${formatDisplayNameIntl(intl, value, true)} - ${formatAddressInline(
                              value.address,
                              intl
                            )}`}
                          </option>
                        ))}
                        <option value="CUSTOM">
                          {intl.formatMessage({
                            id: "CUSTOM.ADDRESS",
                          })}
                        </option>
                      </Select>
                    </div>
                  </div>
                  {lead.invoiceAddressType === "CUSTOM" && (
                    <>
                      <div className="form-group row">
                        <div className="col-lg-12">
                          <Field
                            name="invoiceAddress.name"
                            data-cy="input-lead-address-name"
                            component={Input}
                            onChange={(e) => customHandleChange(e)(handleChange)}
                            placeholder={intl.formatMessage({
                              id: "COMMON.NAME",
                            })}
                            label={intl.formatMessage({
                              id: "COMMON.NAME",
                            })}
                            disabled={disabled}
                          />
                        </div>
                      </div>
                      <AddressEditForm
                        customHandleChange={customHandleChange}
                        handleChange={handleChange}
                        disabled={disabled}
                        prefix="invoiceAddress."
                      />
                    </>
                  )}
                </>
              )}
            </Form>
          </>
        )}
      </Formik>
    </>
  );
}
